import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../lib/cn'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'dark'
  size?: 'sm' | 'md' | 'lg'
}

const variants: Record<string, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 shadow-[0_1px_0_0_rgba(0,0,0,0.05)]',
  secondary: 'bg-shell text-shell-text-active border border-shell-border hover:bg-shell-border/40 active:bg-shell-border/60',
  ghost: 'bg-transparent text-shell-text hover:bg-shell-border/50 active:bg-shell-border/70',
  danger: 'bg-danger-500 text-white hover:bg-danger-600 active:bg-danger-700',
  dark: 'bg-ink-950 text-white hover:bg-ink-800 active:bg-ink-700',
}

const sizes: Record<string, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-sm gap-2 rounded-xl',
  lg: 'h-12 px-6 text-sm gap-2 rounded-xl',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none disabled:active:scale-100 whitespace-nowrap',
          variants[variant],
          sizes[size],
          className,
        )}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'
